import type { ComponentType } from "react";
import type {
  WidgetAudience,
  WidgetCatalogEntry,
  WidgetDataDomain,
  WidgetDefinition,
  WidgetLayoutHints,
  WidgetLinks,
  WidgetModule,
  WidgetProps,
  WidgetData,
  WidgetSettings,
  WidgetSubject,
  WidgetTitleVariable,
} from "@nexus/widget-sdk";
import { widgetSettingsOf } from "@nexus/widget-sdk";
import { widgetDefinitions } from "@nexus/widgets";

// ── Public types (re-exported for consumers that used to import from here) ────
export type { WidgetAudience };

export interface AvailableWidget {
  id: string;
  source: "local";
  manifest: WidgetDefinition["module"]["manifest"];
  defaultConfig: unknown;
  defaultLayoutClassName: string;
  layout?: WidgetDefinition["layout"];
  description: string;
  /** Where its data comes from (see `WidgetCatalogEntry.domain`). */
  domain: WidgetDataDomain;
  audiences: readonly WidgetAudience[];
  /** Logical widget this definition is a new version of (see `WidgetCatalogEntry.versionOf`). */
  versionOf?: string;
  versionLabel?: string;
}

// ── Registry — assembled from widget definitions at module load time ──────────

const WIDGET_REGISTRY = new Map<string, WidgetDefinition>(widgetDefinitions.map((d) => [d.id, d]));

// ── Public API ────────────────────────────────────────────────────────────────

export function getWidget(widgetId: string): WidgetModule | null {
  const entry = WIDGET_REGISTRY.get(widgetId);
  if (!entry) return null;
  // Cast component to the erased WidgetProps<unknown> the host requires.
  return {
    ...entry.module,
    Component: entry.module.Component as ComponentType<WidgetProps<unknown>>,
  };
}

export function getDefaultLayoutClassName(widgetId: string): string {
  return WIDGET_REGISTRY.get(widgetId)?.defaultLayoutClassName ?? "col-span-12";
}

/** How a bundled widget presents itself in the library, `undefined` for unknown ids. */
export function getWidgetCatalogEntry(widgetId: string): WidgetCatalogEntry | undefined {
  return WIDGET_REGISTRY.get(widgetId)?.catalog;
}

/**
 * What an author can set on a widget, as the widget declares it (a JSON Schema
 * and its overlay). A widget declaring none has its frame's settings. The editor
 * renders them without knowing the widget.
 */
export function getWidgetSettings(widgetId: string): WidgetSettings {
  return widgetSettingsOf(WIDGET_REGISTRY.get(widgetId) ?? { id: widgetId });
}

/** The variables a widget fills in its own title (see `WidgetDefinition.titleVariables`). */
export function getWidgetTitleVariables(widgetId: string): readonly WidgetTitleVariable[] {
  return WIDGET_REGISTRY.get(widgetId)?.titleVariables ?? [];
}

/** The host-coordinated relationships a widget declares (see `WidgetLinks`). */
export function getWidgetLinks(widgetId: string): WidgetLinks {
  return WIDGET_REGISTRY.get(widgetId)?.links ?? {};
}

/**
 * What an instance's loaded data says the page is about (an event, a campaign),
 * as its widget declares it (`WidgetDefinition.subjects`). Never throws.
 */
export function getWidgetSubjects(
  widgetId: string,
  data: WidgetData | undefined,
): readonly WidgetSubject[] {
  const subjects = WIDGET_REGISTRY.get(widgetId)?.subjects;
  if (!subjects || !data) return [];
  try {
    return subjects(data);
  } catch {
    return [];
  }
}

/**
 * How the host lays out and waits for a widget instance: the definition's hints,
 * with those that depend on the instance's configuration applied.
 */
export function getWidgetLayout(
  widgetId: string,
  config: Readonly<Record<string, unknown>> = {},
): WidgetLayoutHints {
  const layout = WIDGET_REGISTRY.get(widgetId)?.layout;
  if (!layout) return {};
  const { byConfig, ...hints } = layout;
  return { ...hints, ...byConfig?.(config as Parameters<typeof byConfig>[0]) };
}

export function listWidgetImplementations(): AvailableWidget[] {
  return widgetDefinitions.map((d) => ({
    id: d.id,
    source: "local" as const,
    manifest: d.module.manifest,
    defaultConfig: d.module.defaultConfig ?? {},
    defaultLayoutClassName: d.defaultLayoutClassName,
    layout: d.layout,
    description: d.catalog.description,
    domain: d.catalog.domain,
    audiences: d.catalog.audiences,
    versionOf: d.catalog.versionOf,
    versionLabel: d.catalog.versionLabel,
  }));
}
